import {
  Alert,
  Anchor,
  Badge,
  Button,
  FileInput,
  Group,
  Modal,
  Stack,
  Text,
  Textarea,
} from '@mantine/core';
import { IconBulb, IconPaperclip, IconSend, IconTrash } from '@tabler/icons-react';
import { useState } from 'react';

import { apiDownload, apiFetch } from '@/api/client';
import { QueryState } from '@/components/feedback';
import { formatDateTime } from '@/lib/format';
import { useApiMutation } from '@/lib/mutations';

import {
  ACCEPTED_FILES,
  fileSize,
  MY_KEY,
  type Recommendation,
  STATUS_COLOR,
  useMyRecommendation,
} from './api';

function Form({ current, onDone }: { current: Recommendation | null; onDone: () => void }) {
  const [text, setText] = useState(current?.text ?? '');
  const [file, setFile] = useState<File | null>(null);
  const [removeFile, setRemoveFile] = useState(false);
  const save = useApiMutation(
    () => {
      const body = new FormData();
      body.append('text', text);
      if (file) body.append('file', file);
      if (removeFile && !file) body.append('remove_file', 'true');
      return apiFetch('/recommandation', { method: 'PUT', body });
    },
    {
      invalidate: [MY_KEY],
      success: 'Merci ! Votre recommandation a été transmise au développeur.',
      onSuccess: onDone,
    },
  );
  const keptFile = current?.file_name && !removeFile ? current : null;
  const empty = !text.trim() && !file && !keptFile;

  return (
    <Stack>
      <Alert color="yellow" variant="light" icon={<IconBulb size={20} />}>
        <Text size="sm" fw={600}>
          Ce menu vous est adressé : aidez-nous à améliorer le logiciel.
        </Text>
        <Text size="sm">
          Dites-nous ce qui vous gêne, ce qui manque ou ce qui pourrait être plus simple dans votre
          travail. Votre recommandation est envoyée directement au développeur du logiciel et n'est
          visible que par lui : ni vos collègues ni la direction de la clinique ne la voient.
        </Text>
        <Text size="sm" mt={4}>
          Vous avez une seule recommandation : vous pouvez la compléter ou la modifier à tout
          moment.
        </Text>
      </Alert>
      {current && (
        <Group gap="xs">
          <Badge color={STATUS_COLOR[current.status]} variant="light">
            {current.status_label}
          </Badge>
          <Text size="xs" c="dimmed">
            Enregistrée le {formatDateTime(current.updated_at)}
          </Text>
        </Group>
      )}
      {current?.editor_note && (
        <Alert color="teal" variant="light" title="Réponse du développeur">
          {current.editor_note}
        </Alert>
      )}
      <Textarea
        label="Votre recommandation"
        placeholder={
          'Exemples : « À l’accueil, il faudrait pouvoir… », « Le rapport de caisse devrait ' +
          'afficher… », « Il manque un menu pour… »'
        }
        autosize
        minRows={8}
        maxRows={18}
        maxLength={20000}
        value={text}
        onChange={(e) => setText(e.currentTarget.value)}
      />
      {keptFile && !file && (
        <Group gap="xs">
          <IconPaperclip size={16} />
          <Anchor
            component="button"
            size="sm"
            onClick={() =>
              void apiDownload('/recommandation/fichier', { filename: keptFile.file_name })
            }
          >
            {keptFile.file_name}
          </Anchor>
          <Text size="xs" c="dimmed">
            {fileSize(keptFile.file_size)}
          </Text>
          <Button
            size="compact-xs"
            variant="subtle"
            color="red"
            leftSection={<IconTrash size={14} />}
            onClick={() => setRemoveFile(true)}
          >
            Retirer
          </Button>
        </Group>
      )}
      <FileInput
        label={keptFile ? 'Remplacer le fichier joint' : 'Ou joindre un fichier (facultatif)'}
        description="Document Word, PDF, Excel, texte ou photo d'une page (10 Mo au plus)."
        placeholder="Choisir un fichier"
        accept={ACCEPTED_FILES}
        leftSection={<IconPaperclip size={16} />}
        clearable
        value={file}
        onChange={setFile}
      />
      <Group justify="flex-end">
        <Button
          leftSection={<IconSend size={16} />}
          disabled={empty}
          loading={save.isPending}
          onClick={() => save.mutate(undefined)}
        >
          {current ? 'Enregistrer les modifications' : 'Envoyer ma recommandation'}
        </Button>
      </Group>
    </Stack>
  );
}

/** Fenêtre « Recommandations » : la recommandation de l'utilisateur au développeur. */
export function RecommendationModal({ opened, onClose }: { opened: boolean; onClose: () => void }) {
  const mine = useMyRecommendation(opened);
  return (
    <Modal opened={opened} onClose={onClose} title="Recommandations" size="lg">
      {opened && (
        <QueryState query={mine}>
          {(current) => (
            <Form key={current?.updated_at ?? 'new'} current={current} onDone={onClose} />
          )}
        </QueryState>
      )}
    </Modal>
  );
}
